import { useState, useEffect } from "react";
import swal from 'sweetalert2';
import * as helper from '../global/helper';
import { postVendorFeeCalendar } from "../api/vendorAPI";

// TypeID: 1 = Weekday, 2 = Weekend  ×  Hole: 9 / 18  →  4 fee rows per calendar.
// Order here drives the table row order: 18 Weekday, 18 Weekend, 9 Weekday, 9 Weekend.
const FEE_TYPES = [
    { TypeID: 1, Hole: 18, label: 'Weekday · 18 Hole', dayLabel: 'Weekday' },
    { TypeID: 2, Hole: 18, label: 'Weekend · 18 Hole', dayLabel: 'Weekend' },
    { TypeID: 1, Hole: 9,  label: 'Weekday · 9 Hole',  dayLabel: 'Weekday' },
    { TypeID: 2, Hole: 9,  label: 'Weekend · 9 Hole',  dayLabel: 'Weekend' },
];

// Unique key for a (type, hole) combination.
const feeKey = (typeID, hole) => `${typeID}-${hole}`;

const emptyFee = (TypeID, Hole) => ({
    TypeID,
    Hole,
    GreenFee   : 0,
    CartFee    : 0,
    CaddyFee   : 0,
    TaxPercent : 18,
    Comment    : '',
});

const defaultCalendar = {
    Name      : '',
    StartDate : '',
    EndDate   : '',
    IsActive  : 1,
    Comment   : '',
};

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Mirrors backend computeFee — subtotal = Green+Cart+Caddy, Tax = subtotal*pct/100,
// Total = subtotal + Tax. Tax and Total are rounded to whole numbers (final calc).
const computeFee = (fee) => {
    const green = Number(fee.GreenFee)   || 0;
    const cart  = Number(fee.CartFee)    || 0;
    const caddy = Number(fee.CaddyFee)   || 0;
    const pct   = Number(fee.TaxPercent) || 0;
    const subtotal = green + cart + caddy;
    // const tax      = Math.round(subtotal * pct / 100);
    const tax = Number((subtotal * pct / 100).toFixed(2));
    
    return { Tax: tax, Total: Math.round(subtotal + tax) };
};

// Backend DATE comes back as an ISO string — keep just YYYY-MM-DD for <input type="date">
const toDateInput = (d) => (d ? String(d).slice(0, 10) : '');

// A calendar has "started" when today is on/after its StartDate. Once started,
// only End Date and Comment can be edited (revise fees by ending it & adding new).
const isCalendarStarted = (cal) => {
    if (!cal || !cal.StartDate) return false;
    const start = new Date(toDateInput(cal.StartDate));
    start.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return start <= today;
};

const AddFeeCalendarModal = ({ show, onDismissModal, vendorID, feeCalendar, onSave }) => {

    const [calendar, setCalendar] = useState(defaultCalendar);
    const [fees,     setFees]     = useState(FEE_TYPES.map((t) => emptyFee(t.TypeID, t.Hole)));

    useEffect(() => {
        if (show) {
            if (feeCalendar) {
                setCalendar({
                    ID        : feeCalendar.ID,
                    Name      : feeCalendar.Name || '',
                    StartDate : toDateInput(feeCalendar.StartDate),
                    EndDate   : toDateInput(feeCalendar.EndDate),
                    IsActive  : feeCalendar.IsActive ? 1 : 0,
                    Comment   : feeCalendar.Comment || '',
                });
                // Seed each (type, hole) from existing Fees[], falling back to zeros
                setFees(FEE_TYPES.map((t) => {
                    const existing = (feeCalendar.Fees || []).find((f) => f.TypeID === t.TypeID && Number(f.Hole) === t.Hole);
                    return existing
                        ? {
                            TypeID     : t.TypeID,
                            Hole       : t.Hole,
                            GreenFee   : existing.GreenFee   ?? 0,
                            CartFee    : existing.CartFee    ?? 0,
                            CaddyFee   : existing.CaddyFee   ?? 0,
                            TaxPercent : existing.TaxPercent ?? 18,
                            Comment    : existing.Comment    || '',
                        }
                        : emptyFee(t.TypeID, t.Hole);
                }));
            } else {
                setCalendar(defaultCalendar);
                setFees(FEE_TYPES.map((t) => emptyFee(t.TypeID, t.Hole)));
            }

            $('#fee-calendar-modal').modal({ backdrop: 'static' });
            $('#fee-calendar-modal').on('hidden.bs.modal', function () {
                onDismissModal();
            });
            $('#fee-calendar-modal').modal('show');
        } else {
            $('#fee-calendar-modal').modal('hide');
        }
    }, [show]);

    // Editing a master that has already started → lock everything except End Date + Comment.
    const started = !!(feeCalendar && feeCalendar.ID) && isCalendarStarted(feeCalendar);
    // const started = false;

    const updateCalendar = (field, value) => {
        setCalendar((c) => ({ ...c, [field]: value }));
    };

    const updateFee = (typeID, hole, field, value) => {
        setFees((prev) => prev.map((f) => (f.TypeID === typeID && f.Hole === hole ? { ...f, [field]: value } : f)));
    };

    const saveHandler = async () => {
        const btn  = $('#btnSaveFeeCalendar');
        const form = $('#add-fee-calendar-form');

        form.validate({
            rules: {
                txtName      : { required: true },
                txtStartDate : { required: true },
                txtEndDate   : { required: true },
            },
            messages: {
                txtName      : { required: "Please enter calendar name" },
                txtStartDate : { required: "Please select start date" },
                txtEndDate   : { required: "Please select end date" },
            },
        });

        if (!form.valid()) return;

        // Client-side: Start <= End
        if (new Date(calendar.StartDate) > new Date(calendar.EndDate)) {
            swal.fire({
                icon      : 'warning',
                titleText : 'Start Date must be on or before End Date.',
                showConfirmButton : false,
                timer     : 2000,
            });
            return;
        }

        // Client-side: no negative fees
        const hasNegative = fees.some((f) =>
            [f.GreenFee, f.CartFee, f.CaddyFee, f.TaxPercent].some((v) => Number(v) < 0));
        if (hasNegative) {
            swal.fire({
                icon      : 'warning',
                titleText : 'Fee values cannot be negative.',
                showConfirmButton : false,
                timer     : 2000,
            });
            return;
        }

        helper.StartProcessing(btn);

        const isEdit = !!calendar.ID;

        try {
            const calendarPayload = {
                ID        : calendar.ID || 0,
                VendorID  : vendorID,
                Name      : calendar.Name,
                StartDate : calendar.StartDate,
                EndDate   : calendar.EndDate,
                IsActive  : calendar.IsActive,
                Comment   : calendar.Comment || '',
            };

            const feesPayload = fees.map((f) => ({
                TypeID     : f.TypeID,
                Hole       : f.Hole,
                GreenFee   : Number(f.GreenFee)   || 0,
                CartFee    : Number(f.CartFee)    || 0,
                CaddyFee   : Number(f.CaddyFee)   || 0,
                TaxPercent : Number(f.TaxPercent) || 0,
                Comment    : f.Comment || '',
            }));

            await postVendorFeeCalendar(calendarPayload, feesPayload);
            helper.StopProcessing(btn);
            onDismissModal();
            onSave();

            swal.fire({
                toast             : true,
                icon              : 'success',
                titleText         : isEdit ? 'Fee calendar updated successfully!' : 'Fee calendar added successfully!',
                position          : 'bottom-end',
                showConfirmButton : false,
                timer             : 1500,
                animation         : false,
                customClass       : { popup: 'margin-20' },
            });
        } catch (err) {
            helper.StopProcessing(btn);
            swal.fire({
                icon               : 'error',
                titleText          : 'Error!',
                text               : err.message,
                buttonsStyling     : false,
                confirmButtonClass : 'btn btn-brand',
            });
        }
    };

    return (
        <div className="modal fade" id="fee-calendar-modal" tabIndex="1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-lg " role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{calendar.ID ? 'Edit Fee Calendar' : 'Add Fee Calendar'}</h5>
                        <button type="button" className="close" data-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <form className="kt-form" id="add-fee-calendar-form">
                            <div className="kt-form kt-form--label-right padding-10 padding-t0" role="form">

                                {/* Started master notice — only End Date & Comment editable */}
                                {started && (
                                    <div className="alert alert-secondary fade show mb-3 p-1 px-3" role="alert">
                                        <div className="alert-icon"><i className="flaticon-warning"></i></div>
                                        <div className="alert-text">
                                            This fee calendar has already started, so only its <b>End Date</b> and <b>Comment</b> can be changed.
                                            To revise the fees, set an End Date to end this calendar and add a new one.
                                        </div>
                                    </div>
                                )}

                                {/* Name */}
                                <div className="form-group row mb-3">
                                    <label className="col-form-label col-lg-2">Name <span className="red">*</span></label>
                                    <div className="col-lg-9">
                                        <input
                                            id="txtName"
                                            name="txtName"
                                            type="text"
                                            className="form-control"
                                            value={calendar.Name}
                                            disabled={started}
                                            onChange={(e) => updateCalendar('Name', e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Dates + Active */}
                                <div className="form-group row mb-1">
                                    <label htmlFor="txtStartDate" className="col-form-label col-lg-2">Start Date <span className="red">*</span></label>
                                    <div className="col-lg-3">
                                        <input
                                            id="txtStartDate"
                                            name="txtStartDate"
                                            type="date"
                                            className="form-control"
                                            value={calendar.StartDate}
                                            disabled={started}
                                            onChange={(e) => updateCalendar('StartDate', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-lg-4 d-flex">
                                        <label htmlFor="txtEndDate" style={{width:"150px"}} className="col-form-label mr-2">End Date <span className="red">*</span></label>
                                        <input
                                            id="txtEndDate"
                                            name="txtEndDate"
                                            type="date"
                                            className="form-control d-flex"
                                            value={calendar.EndDate}
                                            onChange={(e) => updateCalendar('EndDate', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-lg-2 d-flex">
                                        <label htmlFor="ipActive" className="col-form-label mr-2 ">Active</label>
                                        <span className="kt-switch kt-switch--icon">
                                            <label>
                                                <input
                                                    id="ipActive"
                                                    type="checkbox"
                                                    checked={!!calendar.IsActive}
                                                    // disabled={started}
                                                    onChange={(e) => updateCalendar('IsActive', e.target.checked ? 1 : 0)}
                                                />
                                                <span></span>
                                            </label>
                                        </span>
                                    </div>
                                </div>

                                {/* Comment */}
                                <div className="form-group row mb-0">
                                    <label htmlFor="txtcomment" className="col-form-label col-lg-2">Comment</label>
                                    <div className="col-lg-9">
                                        <textarea
                                            id="txtcomment"
                                            className="form-control"
                                            rows={2}
                                            value={calendar.Comment}
                                            onChange={(e) => updateCalendar('Comment', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <hr className="" />

                                {/* Fee table — fixed 4 rows: Hole × Day Type (18 WD/WE, 9 WD/WE) */}
                                <div className="table-responsive">
                                    <table className="table table-bordered mb-0 align-middle vendor-fee-table">
                                        <thead className="kt-font-bold">
                                            <tr>
                                                <th style={{ width: '60px' }}>Hole</th>
                                                <th style={{ width: '110px' }}>Day Type</th>
                                                <th>Green Fee</th>
                                                <th>Cart Fee</th>
                                                <th>Caddy Fee</th>
                                                <th style={{ width: '90px' }}>Tax %</th>
                                                <th style={{ width: '100px' }}>Tax</th>
                                                <th style={{ width: '110px' }}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {FEE_TYPES.map((t) => {
                                                const fee = fees.find((f) => f.TypeID === t.TypeID && f.Hole === t.Hole) || emptyFee(t.TypeID, t.Hole);
                                                const { Tax, Total } = computeFee(fee);
                                                return (
                                                    <tr key={feeKey(t.TypeID, t.Hole)}>
                                                        <td className="kt-font-bold align-middle">{t.Hole}</td>
                                                        <td className="align-middle">{t.dayLabel}</td>
                                                        <td>
                                                            <input
                                                                type="number" min="0" step="0.01"
                                                                className="form-control form-control-sm"
                                                                value={fee.GreenFee}
                                                                disabled={started}
                                                                onChange={(e) => updateFee(t.TypeID, t.Hole, 'GreenFee', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number" min="0" step="0.01"
                                                                className="form-control form-control-sm"
                                                                value={fee.CartFee}
                                                                disabled={started}
                                                                onChange={(e) => updateFee(t.TypeID, t.Hole, 'CartFee', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number" min="0" step="0.01"
                                                                className="form-control form-control-sm"
                                                                value={fee.CaddyFee}
                                                                disabled={started}
                                                                onChange={(e) => updateFee(t.TypeID, t.Hole, 'CaddyFee', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number" min="0" step="0.01"
                                                                className="form-control form-control-sm"
                                                                value={fee.TaxPercent}
                                                                disabled={started}
                                                                onChange={(e) => updateFee(t.TypeID, t.Hole, 'TaxPercent', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input type="text" className="form-control form-control-sm" value={Tax} readOnly disabled />
                                                        </td>
                                                        <td>
                                                            <input type="text" className="form-control form-control-sm kt-font-bolder" value={Total} readOnly disabled />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" data-dismiss="modal">Cancel</button>
                        <button id="btnSaveFeeCalendar" type="button" className="btn btn-brand" onClick={saveHandler}>Save</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddFeeCalendarModal;
